/* eslint-disable @typescript-eslint/no-explicit-any */
import uuid4 from 'uuid4';
import {
  Tabata,
  TabataExercise,
  HIIT,
  HIITExercise,
  Exercise,
  ParentExercise,
  SubExercise,
  Workout,
} from './workouts.domain';
import {
  AutoplayedExerciseCollection,
  ExerciseBase,
  ExerciseDTO,
  HIITDTO,
  HIITExerciseDTO,
  SubExerciseBase,
  TabataDTO,
  TabataExerciseDTO,
  TabataExerciseSetDTO,
} from '../model/workout.domain';
import { WorkoutVM } from './workout.viewModel';
import { omitObject, pickObject } from '@client/new/helpers/general';
import { SMap, TypedOmit, TypedValue } from '@client/new/helpers/types';
import { UseFormReturn } from 'react-hook-form';
import { defaults } from './workout.schema';

const createExerciseBase = (): TypedOmit<ExerciseBase, 'type'> => ({
  id: uuid4(),
  name: '',
  description: '',
});

const createSubExerciseBase = <T>(
  sets: T[] = [],
): TypedOmit<SubExerciseBase<T>, 'type'> => ({
  ...createExerciseBase(),
  media: {},
  sets,
});

const createAutoplayedExerciseCollection = (
  delta?: Partial<AutoplayedExerciseCollection<any>>,
) => ({
  ...createExerciseBase(),
  rounds: 1,
  goal: 'time',
  exercises: [],
  longRest: defaults.longRest.default,
  ...delta,
});

export const createTabata = (delta?: Partial<Tabata['value']>): Tabata => ({
  type: 'Tabata',
  value: omitObject(createAutoplayedExerciseCollection(delta), [
    'type',
    'goal',
  ]),
});

export const createHIIT = (delta?: Partial<HIIT['value']>): HIIT => ({
  type: 'HIIT',
  value: omitObject(createAutoplayedExerciseCollection(delta), ['type']),
});

export const toTabata = (v: TypedOmit<TabataDTO, 'type'>) =>
  createTabata({
    ...pickObject(v, ['description', 'id', 'name', 'rounds', 'longRest']),
    exercises: v.exercises.map((v) => toTabataExercise(v)),
  });

const createSubExercise = <
  T extends TabataExercise['value'] | HIITExercise['value'],
>(
  delta?: Partial<T>,
): T =>
  ({
    ...createSubExerciseBase([toSet()]),
    ...delta,
  } as T);

export const toTabataExercise = (
  delta?: Partial<TabataExercise['value']>,
): TabataExercise => ({
  type: 'TabataExercise',
  value: createSubExercise(delta),
});

export const toSubExercise = (v: TabataExerciseDTO | HIITExerciseDTO) =>
  v.type === 'TabataExercise' ? toTabataExercise(v) : toHIITExercise(v);

export const toHIIT = (v: TypedOmit<HIITDTO, 'type'>): HIIT =>
  createHIIT({
    ...pickObject(v, [
      'description',
      'id',
      'name',
      'rounds',
      'goal',
      'longRest',
    ]),
    exercises: v.exercises.map(toHIITExercise),
  });

export const toHIITExercise = (
  delta?: Partial<HIITExercise['value']>,
): HIITExercise => ({
  type: 'HIITExercise',
  value: createSubExercise(delta),
});

export const toSet = (
  delta?: Partial<TabataExerciseSetDTO>,
): TabataExerciseSetDTO => ({
  rep: delta?.rep || { type: 'AMRAP' },
  time: delta?.time || defaults.time.default,
  rest: delta?.rest || defaults.rest.default,
});

export const toExerciseMap = (
  exercises: ParentExercise[],
): WorkoutVM['exerciseMap'] => {
  const exerciseMap: Workout['exerciseMap'] = {};

  exercises.forEach((e) => {
    if (e.type !== 'HIIT' && e.type !== 'Tabata') return;
    exerciseMap[e.value.id] = e;
    e.value.exercises.forEach(
      (ex) =>
        (exerciseMap[ex.value.id] = {
          type: e.type === 'HIIT' ? 'HIITExercise' : 'TabataExercise',
          value: ex.value,
        } as SubExercise),
    );
  });
  return exerciseMap;
};

export const toExerciseIds = (
  exercises: ParentExercise[],
): WorkoutVM['exerciseIds'] =>
  exercises.flatMap((t) => [
    {
      id: t.value.id,
      items: t.value.exercises.map((ex) => ({ id: ex.value.id })),
    },
  ]);

export const toParentExercise = (ex: ExerciseDTO): ParentExercise => {
  switch (ex.type) {
    case 'Tabata':
      return toTabata(ex);
    case 'HIIT':
      return toHIIT(ex);
  }
};

export const isParentExercise = (ex: Exercise): ex is ParentExercise =>
  ex && (ex.type === 'Tabata' || ex.type === 'HIIT');

export const appendExercises = <T extends ParentExercise>(
  parent: T,
  newExercises: SubExercise[],
): T => ({
  ...parent,
  value: {
    ...parent.value,
    exercises: [...parent.value.exercises, ...newExercises],
  },
});

export const materializeMap = <T extends TypedValue>(vm: {
  exerciseMap: SMap<T>;
  formMap: SMap<UseFormReturn>;
}) =>
  Object.entries(vm.exerciseMap).reduce(
    (acc, [id, { type }]) => ({
      ...acc,
      [id]: { type, value: vm.formMap[id].getValues() } as T,
    }),
    {} as SMap<T>,
  );

export const toParentExercises = <
  T1 extends TypedValue,
  T2 extends TypedValue = ParentExercise,
>(
  vm: Pick<Workout, 'exerciseIds'> & {
    exerciseMap: SMap<T1>;
    formMap: SMap<UseFormReturn>;
  },
): T2[] => {
  const valuesMap = materializeMap(vm) as any as SMap<T2>;
  return vm.exerciseIds.map((p) => {
    const parent = valuesMap[p.id];
    parent.value.exercises = p.items.map(({ id }) => valuesMap[id]) as any;
    return parent;
  });
};

export const getErrors = (formMap: SMap<UseFormReturn>): SMap<string> =>
  Object.entries(formMap).reduce((acc, [id, f]) => {
    const errorMessages = Object.values(f.formState.errors)
      .filter((e) => e?.message)
      .map((e) => e?.message as string);

    if (errorMessages.length > 0) {
      acc[id] = errorMessages.join(', ');
    }
    return acc;
  }, {} as SMap<string>);
